import { createContext, useContext } from 'react';

/**
 * EAA-1776 (§1b): fail-closed console capability context.
 *
 * Mirrors the monorepo's `packages/hooks` AuthCapabilityContext by shape (the
 * portal repo is standalone, so the contract is duplicated, not imported):
 * with NO provider mounted — or an empty set — every `can()` answers false.
 * The set itself comes exclusively from `GET /me/admin`'s server-computed
 * `capabilities[]` (D10: the FE never derives permissions from token claims or
 * `is_super_admin`). Hiding is UX only — portal-api re-checks every call.
 */
export interface ConsoleCapability {
    can(key: string): boolean;
}

const failClosed: ConsoleCapability = { can: () => false };

export const ConsoleCapabilityContext = createContext<ConsoleCapability>(failClosed);
ConsoleCapabilityContext.displayName = 'ConsoleCapabilityContext';

/** Resolve a server capability list into the fail-closed `can()` shape. */
export function resolveCapabilities(capabilities?: string[] | null): ConsoleCapability {
    const set = new Set(capabilities ?? []);
    return { can: (key: string) => set.has(key) };
}

/** `can(key)` — the only permission check console components may use. */
export function useCan(): (key: string) => boolean {
    return useContext(ConsoleCapabilityContext).can;
}
